import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'react-toastify';
import { waybillsAPI, projectsAPI } from '../../helpers/api';
import BottomSheet from '../../components/Common/BottomSheet';

interface Site {
  id: string;
  name: string;
  project: { name: string };
}

const FormanQRScanner: React.FC = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [showSiteSheet, setShowSiteSheet] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [waybillNumber, setWaybillNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photosPreviews, setPhotosPreviews] = useState<string[]>([]);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchSites();
  }, []);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          const state = scannerRef.current.getState();
          if (state === 2) { // Html5QrcodeScannerState.SCANNING
            scannerRef.current.stop().catch(() => {});
          }
        } catch {
          // Scanner not initialized, ignore
        }
      }
    };
  }, []);

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const startScanning = async () => {
    if (!selectedSite) {
      toast.error('Lütfen önce şantiye seçin');
      return;
    }

    try {
      scannerRef.current = new Html5Qrcode('qr-reader');
      setScanning(true);

      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          setScannedData(decodedText);

          // E-fatura QR kod formatlarını destekle
          let extractedNumber = decodedText.trim();

          // URL formatı ise (GİB doğrulama linki)
          if (extractedNumber.includes('http')) {
            // URL'den ettn veya fatura parametresini çıkar
            const urlMatch = extractedNumber.match(/[?&](ettn|fatura|no|id)=([^&]+)/i);
            if (urlMatch) {
              extractedNumber = urlMatch[2];
            } else {
              // URL path'inden son kısmı al
              const pathMatch = extractedNumber.match(/\/([A-Z0-9-]+)$/i);
              if (pathMatch) {
                extractedNumber = pathMatch[1];
              }
            }
          }

          // E-fatura numarası formatı: ABC2025... veya ZM12025... (2-3 harf + sayılar)
          const invoiceMatch = extractedNumber.match(/[A-Z]{2,3}[0-9]{10,}/i);
          if (invoiceMatch) {
            extractedNumber = invoiceMatch[0];
          }

          // ETTN formatı (UUID): xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
          const ettnMatch = extractedNumber.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
          if (ettnMatch) {
            extractedNumber = ettnMatch[0];
          }

          setWaybillNumber(extractedNumber);
          stopScanning();
          toast.success('QR kod okundu!');
        },
        () => {}
      );
    } catch (error) {
      console.error('Scanner start error:', error);
      toast.error('Kamera açılamadı');
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // Html5QrcodeScannerState.SCANNING
          await scannerRef.current.stop();
        }
      } catch {
        // Ignore errors
      }
      setScanning(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedSite || !waybillNumber) {
      toast.error('Şantiye ve irsaliye numarası zorunludur');
      return;
    }

    try {
      setSubmitting(true);

      // İrsaliyeyi kaydet
      const response = await waybillsAPI.scanQR({
        waybillNumber,
        qrCode: scannedData || waybillNumber,
        siteId: selectedSite,
        notes: notes || undefined,
      });

      const waybillId = response.data.id;

      // Fotoğrafları yükle
      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach((photo) => {
          formData.append('photos', photo);
        });

        try {
          await waybillsAPI.uploadPhotos(waybillId, formData);
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          toast.warning('İrsaliye kaydedildi ancak fotoğraflar yüklenemedi');
        }
      }

      toast.success('İrsaliye kaydedildi!');
      navigate('/forman');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setScannedData('');
    setWaybillNumber('');
    setNotes('');
    setPhotos([]);
    setPhotosPreviews([]);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos = Array.from(files);
    const newPreviews = newPhotos.map((file) => URL.createObjectURL(file));

    setPhotos((prev) => [...prev, ...newPhotos]);
    setPhotosPreviews((prev) => [...prev, ...newPreviews]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photosPreviews[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotosPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="forman-page">
      {/* Header */}
      <div className="forman-page-header">
        <button className="back-btn" onClick={() => navigate('/forman')}>
          <i className="ri-arrow-left-line"></i>
        </button>
        <h1>QR Okut</h1>
      </div>

      {/* Content */}
      <div className="forman-page-content">
        {/* Site Selection */}
        <div className="forman-field">
          <label>Şantiye</label>
          <button
            type="button"
            className={`forman-select-trigger ${selectedSite ? 'has-value' : 'placeholder'}`}
            onClick={() => setShowSiteSheet(true)}
          >
            <span className="forman-select-trigger-text">
              {selectedSite
                ? sites.find((s) => s.id === selectedSite)?.project.name +
                  ' / ' +
                  sites.find((s) => s.id === selectedSite)?.name
                : 'Şantiye seçin...'}
            </span>
            <i className="ri-arrow-down-s-line"></i>
          </button>
        </div>

        <BottomSheet
          isOpen={showSiteSheet}
          onClose={() => setShowSiteSheet(false)}
          title="Şantiye Seçin"
          options={sites.map((site) => ({
            value: site.id,
            label: site.name,
            sublabel: site.project.name,
          }))}
          selectedValue={selectedSite}
          onSelect={(value) => setSelectedSite(value)}
          searchable
        />

        {/* QR Scanner Area */}
        <div className="forman-qr-area">
          <div
            id="qr-reader"
            className={`forman-qr-reader ${scanning ? 'active' : ''}`}
          ></div>

          {!scanning && !scannedData && (
            <button
              className="forman-qr-button"
              onClick={startScanning}
              disabled={!selectedSite}
            >
              <i className="ri-qr-scan-2-line"></i>
              <span>Taramayı Başlat</span>
            </button>
          )}

          {scanning && (
            <button className="forman-qr-close" onClick={stopScanning}>
              <i className="ri-close-line"></i>
              Kapat
            </button>
          )}

          {scannedData && !scanning && (
            <div className="forman-qr-result">
              <div className="forman-qr-result-icon">
                <i className="ri-checkbox-circle-fill"></i>
              </div>
              <div className="forman-qr-result-text">
                <strong>QR Okundu!</strong>
                <span>{waybillNumber}</span>
              </div>
              <button className="forman-qr-retry" onClick={resetForm}>
                <i className="ri-camera-line"></i>
                Yeniden Tara
              </button>
            </div>
          )}
        </div>

        {/* Waybill Number */}
        <div className="forman-field">
          <label>İrsaliye No</label>
          <input
            type="text"
            value={waybillNumber}
            onChange={(e) => setWaybillNumber(e.target.value)}
            placeholder="QR okutun veya manuel girin"
            className="forman-input"
          />
        </div>

        {/* Notes */}
        <div className="forman-field">
          <label>Not (Opsiyonel)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Varsa not ekleyin..."
            className="forman-textarea"
            rows={2}
          />
        </div>

        {/* Photos */}
        <div className="forman-field">
          <label>Fotoğraf (Opsiyonel)</label>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            multiple
            capture="environment"
            onChange={handlePhotoSelect}
            style={{ display: 'none' }}
          />

          <div className="forman-photos">
            {photosPreviews.map((preview, index) => (
              <div key={index} className="forman-photo-item">
                <img src={preview} alt={`Fotoğraf ${index + 1}`} />
                <button
                  type="button"
                  className="forman-photo-remove"
                  onClick={() => removePhoto(index)}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}

            <button
              type="button"
              className="forman-photo-add"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-camera-line"></i>
              <span>Ekle</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Actions */}
      <div className={`forman-bottom-actions ${showSiteSheet ? 'sheet-open' : ''}`}>
        <button
          className="forman-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !waybillNumber || !selectedSite}
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Kaydediliyor...
            </>
          ) : (
            <>
              <i className="ri-check-line"></i>
              Kaydet
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FormanQRScanner;
